// src/components/BannerPrimaVolta.js
// Una riga discreta che compare SOLO la prima volta in assoluto che una persona apre una
// sezione — spiega in una frase cosa fa quella sezione, e sparisce per sempre appena viene
// chiusa. È il pezzo che mancava alla Guida: la guida risponde a chi la cerca, questo arriva
// nel momento in cui serve, senza che nessuno debba cercare nulla.
//
// Il testo è lo stesso della Guida (guidaContenuti.js) — mai due versioni da mantenere.
// La memoria di "già visto" è per utente e per sezione, su Firestore: così vale su qualunque
// dispositivo, non solo sul browser dove è stato chiuso la prima volta.
import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { X, Lightbulb } from "lucide-react";
import { DESCRIZIONI_PER_ID } from "../utils/guidaContenuti";

export default function BannerPrimaVolta({ sezioneId }) {
  const { currentUser } = useAuth();
  const [mostra, setMostra] = useState(false);
  const [errore, setErrore] = useState(null);
  const info = DESCRIZIONI_PER_ID[sezioneId];

  useEffect(() => {
    // Ogni cambio di sezione riparte da zero: senza questo, il banner della sezione
    // precedente resterebbe visibile mentre si controlla la nuova, e una lettura lenta
    // potrebbe sovrascrivere il risultato di quella giusta.
    setMostra(false);
    setErrore(null);
    if (!currentUser?.uid || !info) return;
    let annullato = false;
    getDoc(doc(db, "sezioniViste", currentUser.uid)).then(snap => {
      if (annullato) return;
      const viste = snap.exists() ? (snap.data().sezioni || []) : [];
      if (!viste.includes(sezioneId)) setMostra(true);
    }).catch(() => {}); // se la lettura fallisce non si mostra nulla: mai un banner al posto di un errore
    return () => { annullato = true; };
  }, [currentUser?.uid, sezioneId, info]);

  const chiudi = async () => {
    if (!currentUser?.uid) { setMostra(false); return; }
    try {
      // arrayUnion aggiunge questa sola sezione senza toccare le altre — niente lettura
      // e riscrittura dell'intero elenco, che chiudendo due banner di fila avrebbe potuto
      // cancellare quello salvato un istante prima.
      await setDoc(doc(db, "sezioniViste", currentUser.uid),
        { uid: currentUser.uid, sezioni: arrayUnion(sezioneId) }, { merge: true });
      // Solo ora il banner sparisce: se il salvataggio non riesce, resta visibile e lo si
      // vede subito, invece di far credere che sia stato registrato quando non è vero.
      setMostra(false);
    } catch (e) {
      console.error("Errore salvataggio sezione vista:", e);
      setErrore(e?.code === "permission-denied"
        ? "Non riesco a ricordare questa scelta: le regole del server per questa funzione non sono ancora attive. Contatta chi gestisce Nexiora."
        : "Non riesco a ricordare questa scelta in questo momento — riprova più tardi.");
    }
  };

  if (!mostra || !info) return null;

  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:10,background:"#faf5ff",border:"1px solid #e9d5ff",
      borderRadius:10,padding:"10px 14px",margin:"0 24px 14px",fontSize:12,color:"#5b21b6",lineHeight:1.55}}>
      <Lightbulb size={15} style={{flexShrink:0,marginTop:1,color:"#7c3aed"}}/>
      <div style={{flex:1}}>
        <div><strong>Prima volta qui.</strong> {info.cosa}</div>
        {info.primoGesto && (
          <div style={{marginTop:4,color:"#6d28d9",fontWeight:600}}>→ {info.primoGesto}</div>
        )}
        {errore && (
          <div style={{marginTop:6,color:"#dc2626",fontWeight:600,fontSize:11}}>⚠️ {errore}</div>
        )}
        <button onClick={chiudi} title="Ho capito, non mostrare più"
          style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:4,background:"#fff",border:"1px solid #e9d5ff",
            borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,color:"#6d28d9",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          Ho capito <X size={11}/>
        </button>
      </div>
    </div>
  );
}
